import React, { useEffect, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import Loader from '~/containers/QuoteEstimates/Loader';
import { addAction } from '~/util/datadog_rum';

import { AuthContext } from '../';
import {
  AUTH_ERROR,
  AUTH0_TOKEN_TYPE,
  ANON_TOKEN_TYPE,
  UPDATE_TOKEN,
  AUTH0_REDIRECT_URL,
} from '../constants';

import { ERROR_LOGIN_REQUIRED } from './constants';
import getTokenFromAnonServer from './getTokenFromAnonServer';
import { useGetUserData } from './hooks/useGetUserData';
import useTokenTimeout from './hooks/useTokenTimeout';
import { PG_ERROR_URL } from '~/components/ErrorBoundary';
import { isForceLoginAutologinUsersActivated } from '~/util/optimizelyHelpers';

interface AuthSessionProps {
  children: React.ReactNode;
}

const AuthSession = ({ children }: AuthSessionProps) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const {
    authDispatch,
    auth: { loading, error },
  } = useContext(AuthContext);

  useTokenTimeout();
  useGetUserData();

  useEffect(() => {
    const getAuthToken = async () => {
      try {
        const fullToken = await getAccessTokenSilently({
          detailedResponse: true,
        });

        if (fullToken?.access_token) {
          const { access_token } = fullToken;

          authDispatch({
            type: UPDATE_TOKEN,
            payload: { token: access_token, tokenType: AUTH0_TOKEN_TYPE },
          });

          return access_token;
        }
      } catch (err) {
        let errorMessage = err.message;

        if (err.message === ERROR_LOGIN_REQUIRED) {
          const queryParams = new URLSearchParams(window.location.search);
          const forceLogin = queryParams.get('authenticatedSession') === 'true';
          // No uuid available since we have not yet gotten a token
          const forceLoginActivated = isForceLoginAutologinUsersActivated('');

          if (forceLogin && forceLoginActivated) {
            queryParams.delete('authenticatedSession');

            let url = `${window.location.origin}`;

            if (window.location.pathname !== '/') {
              url = url.concat(window.location.pathname);
            }

            // Retain query params from external API login links
            const firstName = queryParams.get('firstName');
            const is_partner = queryParams.get('is_partner');

            const redirectParams = {
              redirectUri: AUTH0_REDIRECT_URL,
              passwordless: true,
              appState: {
                returnTo: `${url}?${queryParams.toString()}`,
              },
              firstName,
              is_partner,
              product: 'home',
            };

            loginWithRedirect(redirectParams);

            return;
          }

          const anonRes = await getTokenFromAnonServer();

          if (anonRes.status === 'SUCCESS') {
            const { value } = anonRes;

            authDispatch({
              type: UPDATE_TOKEN,
              payload: { token: value.token, tokenType: ANON_TOKEN_TYPE },
            });

            return value.token;
          }

          errorMessage = anonRes.value.message;
        }
        authDispatch({ type: AUTH_ERROR, payload: { error: errorMessage } });
      }
    };

    getAuthToken();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (error) {
      addAction('Auth Token Failed', {
        error,
      });

      window.location.assign(PG_ERROR_URL);
    }
  }, [error]);

  if (loading || error) {
    return <Loader />;
  }

  return <>{children}</>;
};

export default AuthSession;
